import React, { useState, useEffect } from 'react';
import { Play, FileText, Music, X, Calendar, User, Clock, MessageSquare, Eye, EyeOff, Lock, ChevronLeft, ChevronRight } from 'lucide-react';
import Sidebar from '../../Sidebar/Sidebar';
import Navbar from '../../Navbars/UserNavbar';
import { DashboardApi } from '../../../apis/DashboardApi';
import { AxiosResponse } from 'axios';
import { convertUTCDateToLocalDate } from '../../../functions';
import Modal from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { useSelector } from 'react-redux';

interface ReceivedMediaItem {
  id: number | string;
  type: 'delivery' | 'schedule' | 'legacy';
  media_id: number;
  media_name: string;
  media_type: string;
  media_url: string;
  file_size: string;
  sender_name: string;
  sender_id: number;
  message: string | null;
  is_viewed: boolean;
  delivery_date: string;
  has_been_viewed: boolean;
  is_legacy: boolean;
}

interface MediaDetailProps {
  id: number | string;
  type: 'delivery' | 'schedule' | 'legacy';
  media_id: number;
  media_name: string;
  media_type: string;
  media_url: string;
  file_size: string;
  description?: string;
  sender_name: string;
  sender_id: number;
  message: string | null;
  is_viewed: boolean;
  viewed_at?: string;
  delivery_date: string;
  is_legacy: boolean;
}

interface MediaModalProps {
  media: MediaDetailProps | null;
  onClose: () => void;
}

interface MediaCountsProps {
  all: number;
  video: number;
  image: number;
  audio: number;
  document: number;
}

const formatDateTime = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
  } catch (e) {
    console.error("Date formatting error:", e);
    return dateString; // Return original if error
  }
};

const MediaModal: React.FC<MediaModalProps> = ({ media, onClose }) => {
  if (!media) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="relative bg-white rounded-lg max-w-2xl w-full mx-4" onClick={e => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 z-10"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="p-6">
          <h2 className="text-xl font-semibold mb-2">{media.media_name}</h2>
          <div className="flex items-center text-gray-500 text-sm mb-6 gap-4">
            <div className="flex items-center">
              <User className="w-4 h-4 mr-1" />
              <span>From: {media.sender_name}</span>
            </div>
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-1" />
              <span>Received: {formatDateTime(media.delivery_date)}</span>
            </div>
            {media.is_legacy && (
              <div className="flex items-center text-blue-600">
                <Lock className="w-4 h-4 mr-1" />
                <span>Legacy</span>
              </div>
            )}
          </div>

          <div className="mb-6">
            {media.media_type === 'video' && (
              <div className="aspect-video rounded-lg overflow-hidden bg-black">
                <video
                  src={media.media_url}
                  controls
                  className="w-full h-full"
                >
                  Your browser does not support video playback.
                </video>
              </div>
            )}

            {media.media_type === 'image' && (
              <div className="rounded-lg overflow-hidden bg-gray-100 flex justify-center">
                <img
                  src={media.media_url}
                  alt={media.media_name}
                  className="max-h-[60vh] object-contain"
                />
              </div>
            )}

            {media.media_type === 'audio' && (
              <div className="p-6 bg-gray-100 rounded-lg">
                <div className="flex justify-center mb-4">
                  <Music className="w-16 h-16 text-gray-400" />
                </div>
                <audio
                  src={media.media_url}
                  controls
                  className="w-full"
                >
                  Your browser does not support audio playback.
                </audio>
              </div>
            )}

            {media.media_type === 'document' && (
              <div className="rounded-lg overflow-hidden border">
                <iframe
                  src={`https://docs.google.com/viewer?url=${encodeURIComponent(media.media_url)}&embedded=true`}
                  className="w-full h-[500px]"
                  frameBorder="0"
                >
                  Your browser does not support viewing documents.
                </iframe>
              </div>
            )}
          </div>

          {media.message && (
            <div className="mb-6">
              <h3 className="text-sm font-medium mb-2 flex items-center">
                <MessageSquare className="w-4 h-4 mr-1" />
                Message from {media.sender_name}
              </h3>
              <div className="p-4 bg-gray-50 rounded-lg text-gray-700">
                {media.message}
              </div>
            </div>
          )}

          <div className="border-t pt-4 flex justify-between items-center">
            <div className="text-sm text-gray-500">
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                Viewed: {media.viewed_at ? formatDateTime(media.viewed_at) : 'First time viewing'}
              </div>
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ReceivedMedia = () => {
  const [media, setMedia] = useState<ReceivedMediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedMedia, setSelectedMedia] = useState<MediaDetailProps | null>(null);
  const [mediaDetailsModalOpen, setMediaDetailsModalOpen] = useState(false);
  const [demoModalOpen, setDemoModalOpen] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [mediaCounts, setMediaCounts] = useState<MediaCountsProps>({
    all: 0,
    video: 0,
    image: 0,
    audio: 0,
    document: 0
  });

  // Demo video URL for Received Media
  const demoVideoUrl = "https://youtu.be/8BK6W6H-xvs?si=UWV3oj8v7VfTmCBW";

  useEffect(() => {
    fetchReceivedMedia(1, activeFilter);
  }, [activeFilter]);

  const fetchReceivedMedia = async (page: number = 1, type: string = 'all') => {
    setLoading(true);
    try {
      const response: AxiosResponse<any> = await DashboardApi.getReceivedMedia(page, type);

      if (response?.data?.status === 'success') {
        setMedia(response.data.data);

        // Set pagination data
        if (response.data.pagination) {
          setCurrentPage(response.data.pagination.current_page);
          setTotalPages(response.data.pagination.last_page);
          setTotalItems(response.data.pagination.total);
        }

        // Set media counts for filters
        if (response.data.counts) {
          setMediaCounts(response.data.counts);
        }
      }
    } catch (error) {
      console.error('Error fetching received media:', error);
      toast.error('Failed to load received media');
    } finally {
      setLoading(false);
    }
  };

  const handleMediaClick = async (item: ReceivedMediaItem) => {
    try {
      // Convert string IDs to proper format for legacy items
      const id: any = typeof item.id === 'string' ? item.id : item.id.toString();
      const response: AxiosResponse<any> = await DashboardApi.getMediaDetails(id, item.type);

      if (response?.data?.status === 'success') {
        setSelectedMedia(response.data.data);
        setMediaDetailsModalOpen(true);
      }
    } catch (error) {
      console.error('Error fetching media details:', error);
      toast.error('Failed to load media details');
    }
  };

  const handleMarkAsViewed = async (id: number | string, type: 'delivery' | 'schedule' | 'legacy') => {
    try {
      // Convert to string for API call
      const idStr: any = typeof id === 'number' ? id.toString() : id;
      await DashboardApi.markMediaAsViewed(idStr, type);
      fetchReceivedMedia(currentPage, activeFilter); // Refresh with current page and filter
    } catch (error) {
      console.error('Error marking media as viewed:', error);
    }
  };

  const formatSize = (bytes: string): string => {
    const numBytes = parseFloat(bytes);
    if (numBytes < 1024) return `${numBytes}MB`; // If less than 1GB, show in MB
    else if (numBytes < 1024 * 1024) return `${(numBytes / 1024).toFixed(2)}GB`; // If less than 1TB, show in GB
    else return `${(numBytes / (1024 * 1024)).toFixed(2)}TB`; // Otherwise, show in TB
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      fetchReceivedMedia(page, activeFilter);
    }
  };

  const filteredMedia = activeFilter === 'all'
    ? media
    : media.filter(item => item.media_type === activeFilter);

  const getMediaCard = (item: ReceivedMediaItem) => {
    const commonClasses = "relative rounded-lg overflow-hidden bg-gray-100 h-[300px] w-full cursor-pointer";
    const bottomBarClasses = "absolute bottom-0 left-0 right-0 flex justify-between items-center p-3 bg-gradient-to-t from-black/50 to-transparent";
    const unreadBadgeClasses = "absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full";
    const legacyBadgeClasses = "absolute top-2 right-2 flex items-center gap-2 bg-black/30 px-2 py-1 rounded text-white text-xs";

    switch (item.media_type) {
      case 'video':
        return (
          <div className={commonClasses} onClick={() => handleMediaClick(item)}>
            <div className="absolute inset-0 flex items-center justify-center">
              <button className="bg-black/50 rounded-full p-3 hover:bg-black/70">
                <Play className="w-6 h-6 text-white" />
              </button>
            </div>
            {!item.has_been_viewed && (
              <div className={unreadBadgeClasses}>New</div>
            )}
            {item.is_legacy && (
              <div className={legacyBadgeClasses}>
                <Lock className="w-3 h-3" />
                <span>Legacy</span>
              </div>
            )}
            <div className={bottomBarClasses}>
              <div>
                <span className="text-white text-sm block truncate">{item.media_name}</span>
                <span className="text-gray-300 text-xs">From: {item.sender_name}</span>
              </div>
              <button
                className="text-white p-1 hover:bg-white/20 rounded"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMarkAsViewed(item.id, item.type);
                }}
              >
                {item.has_been_viewed ? (
                  <Eye className="w-5 h-5" />
                ) : (
                  <EyeOff className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        );

      case 'image':
        return (
          <div className={commonClasses} onClick={() => handleMediaClick(item)}>
            <div className="w-full h-full bg-cover bg-center" style={{ backgroundImage: `url(${item.media_url})` }}></div>
            {!item.has_been_viewed && (
              <div className={unreadBadgeClasses}>New</div>
            )}
            {item.is_legacy && (
              <div className={legacyBadgeClasses}>
                <Lock className="w-3 h-3" />
                <span>Legacy</span>
              </div>
            )}
            <div className={bottomBarClasses}>
              <div>
                <span className="text-white text-sm block truncate">{item.media_name}</span>
                <span className="text-gray-300 text-xs">From: {item.sender_name}</span>
              </div>
              <button
                className="text-white p-1 hover:bg-white/20 rounded"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMarkAsViewed(item.id, item.type);
                }}
              >
                {item.has_been_viewed ? (
                  <Eye className="w-5 h-5" />
                ) : (
                  <EyeOff className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        );

      case 'audio':
        return (
          <div className={`${commonClasses} bg-white border flex flex-col justify-center items-center`} onClick={() => handleMediaClick(item)}>
            <Music className="w-16 h-16 text-gray-400 mb-4" />
            <div className="w-3/4 h-2 bg-gray-200 rounded-full" />
            {!item.has_been_viewed && (
              <div className={unreadBadgeClasses}>New</div>
            )}
            {item.is_legacy && (
              <div className={legacyBadgeClasses}>
                <Lock className="w-3 h-3" />
                <span>Legacy</span>
              </div>
            )}
            <div className={bottomBarClasses}>
              <div>
                <span className="text-white text-sm block truncate">{item.media_name}</span>
                <span className="text-gray-300 text-xs">From: {item.sender_name}</span>
              </div>
              <button
                className="text-white p-1 hover:bg-white/20 rounded"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMarkAsViewed(item.id, item.type);
                }}
              >
                {item.has_been_viewed ? (
                  <Eye className="w-5 h-5" />
                ) : (
                  <EyeOff className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        );

      case 'document':
        return (
          <div className={`${commonClasses} bg-white border flex flex-col justify-center items-center`} onClick={() => handleMediaClick(item)}>
            <FileText className="w-16 h-16 text-gray-400 mb-4" />
            <span className="text-sm text-gray-500 truncate px-4">{item.media_name}</span>
            {!item.has_been_viewed && (
              <div className={unreadBadgeClasses}>New</div>
            )}
            {item.is_legacy && (
              <div className={legacyBadgeClasses}>
                <Lock className="w-3 h-3" />
                <span>Legacy</span>
              </div>
            )}
            <div className={bottomBarClasses}>
              <div>
                <span className="text-white text-sm block truncate">{item.media_name}</span>
                <span className="text-gray-300 text-xs">From: {item.sender_name}</span>
              </div>
              <button
                className="text-white p-1 hover:bg-white/20 rounded"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMarkAsViewed(item.id, item.type);
                }}
              >
                {item.has_been_viewed ? (
                  <Eye className="w-5 h-5" />
                ) : (
                  <EyeOff className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const filters = [
    { id: 'all', label: 'All' },
    { id: 'video', label: 'Videos' },
    { id: 'image', label: 'Images' },
    { id: 'audio', label: 'Audio' },
    { id: 'document', label: 'Documents' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="flex flex-col md:flex-row">
        <Sidebar />
        <main className="flex-1 p-6 relative md:ml-64">
          <Navbar />
          <div className="p-4 md:p-6">
            <div className="flex justify-between items-center mb-8">
              <h1 className="text-2xl font-semibold">Received Media</h1>

              <div className="flex items-center gap-4">
                {/* Watch Demo Button with Animations */}
                <button
                  onClick={() => setDemoModalOpen(true)}
                  className="group relative px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg font-medium overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-orange-300 animate-pulse"
                  style={{
                    animation: 'pulse 2s infinite, glow 3s ease-in-out infinite alternate'
                  }}
                >
                  {/* Animated background */}
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-orange-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                  {/* Shimmer effect */}
                  <div className="absolute inset-0 -skew-x-12 bg-gradient-to-r from-transparent via-white to-transparent opacity-20 group-hover:animate-shimmer"></div>

                  {/* Button content */}
                  <div className="relative flex items-center gap-2">
                    <Play className="w-4 h-4 group-hover:animate-bounce" />
                    <span className="font-semibold text-sm">Watch Demo</span>
                  </div>

                  {/* Floating dots animation */}
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-ping"></div>
                </button>
              </div>
            </div>

            {/* Filter Tabs with Counts */}
            <div className="flex flex-wrap gap-2 mb-8">
              {filters.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => setActiveFilter(filter.id)}
                  className={`px-6 py-2 rounded-full text-sm transition-colors flex items-center
                  ${activeFilter === filter.id
                      ? 'bg-orange-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  {filter.label}
                  <span className="ml-2 bg-white bg-opacity-20 px-2 py-0.5 rounded-full text-xs">
                    {mediaCounts[filter.id as keyof MediaCountsProps] || 0}
                  </span>
                </button>
              ))}
            </div>

            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
              </div>
            ) : media.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <div className="text-gray-500 mb-2">No media found</div>
                <p className="text-gray-400 text-sm max-w-md">
                  When someone schedules media to be shared with you, it will appear here after the delivery date.
                </p>
              </div>
            ) : (
              <div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {media.map((item) => (
                    <div key={`${item.type}-${item.id}`}>
                      {getMediaCard(item)}
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-8 flex justify-center">
                    <div className="flex space-x-2 items-center">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="p-2 rounded-full bg-gray-100 disabled:opacity-50"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>

                      <div className="flex space-x-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          // Logic to show appropriate page numbers
                          let pageNumber: any;
                          if (totalPages <= 5) {
                            pageNumber = i + 1;
                          } else if (currentPage <= 3) {
                            pageNumber = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNumber = totalPages - 4 + i;
                          } else {
                            pageNumber = currentPage - 2 + i;
                          }

                          return (
                            <button
                              key={i}
                              onClick={() => handlePageChange(pageNumber)}
                              className={`w-10 h-10 rounded-full flex items-center justify-center ${currentPage === pageNumber
                                ? 'bg-orange-500 text-white'
                                : 'bg-gray-100'
                                }`}
                            >
                              {pageNumber}
                            </button>
                          );
                        })}
                      </div>

                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="p-2 rounded-full bg-gray-100 disabled:opacity-50"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Showing X of Y items */}
                <div className="mt-4 text-center text-sm text-gray-500">
                  Showing {media.length} of {totalItems} items
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Demo Video Modal */}
      <Modal
        open={demoModalOpen}
        onClose={() => setDemoModalOpen(false)}
        center
        classNames={{
          overlay: 'bg-black bg-opacity-75',
          modal: 'bg-transparent shadow-none max-w-4xl w-full p-0 m-4'
        }}
      >
        <div className="relative bg-black rounded-lg overflow-hidden">
          {/* Close button */}
          <button
            onClick={() => setDemoModalOpen(false)}
            className="absolute top-4 right-4 z-10 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Video container */}
          <div className="relative w-full h-0" style={{ paddingBottom: '56.25%' }}>
            <video
              src={'https://res.cloudinary.com/dobeex527/video/upload/v1752610743/copy_3C15CFA7-18BC-4106-8CB9-1BC8EA7BFAA7_dkyvh4.mov'}
              className="absolute top-0 left-0 w-full h-full object-contain"
              controls
              preload="metadata"
              poster=""
              title="Add Recipients Demo Video"
            >
              Your browser does not support the video tag.
            </video>
          </div>

          {/* Video title */}
          <div className="bg-black text-white p-4">
            <h3 className="text-lg font-semibold">Received Media Demo</h3>
            <p className="text-gray-300 text-sm mt-1">
              Learn how to view and manage media content that has been shared with you
            </p>
          </div>
        </div>
      </Modal>

      {/* Media Details Modal */}
      {mediaDetailsModalOpen && selectedMedia && (
        <MediaModal
          media={selectedMedia}
          onClose={() => {
            setMediaDetailsModalOpen(false);
            fetchReceivedMedia(currentPage, activeFilter); // Refresh with current page and filter
          }}
        />
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />


    </div>
  );
};

export default ReceivedMedia;